import {
  Avatar,
  Box,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import { DeleteOutlined, EditOutlined, Fastfood } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import Image from 'material-ui-image';
import { useObserver } from 'mobx-react-lite';
import React, { FC } from 'react';
import QuantityField from '../QuantityField/QuantityField';
import { OrderItem as OI, OrderModifier, OrderOption } from 'kiolyn-api';
import { isNullOrUndefined } from 'util';
import CurrencyFormat from '../../Core/CurrencyFormat/CurrencyFormat';
import { roundM } from '../../../domain/Money';

const imageSize = 40;
const useStyles = makeStyles(theme => ({
  orderItem: {
    paddingTop: theme.spacing(1),
    minHeight: theme.spacing(6),
    minWidth: theme.spacing(40)
  },
  info: {
    flexWrap: 'nowrap'
  },
  detail: {
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    flexGrow: 1,
    flexWrap: 'nowrap'
  },
  detailRow: {
    flexGrow: 1,
    flexWrap: 'nowrap'
  },
  avatar: {
    width: imageSize,
    height: imageSize
  },
  name: {
    flexGrow: 1
  },
  amount: {
    minWidth: 72
  },
  actions: {
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: 8
  },
  actionsSep: {
    flexGrow: 1
  },
  actionButton: {
    marginTop: 4
  }
}));

interface OrderItemProps {
  onDelete?: (orderItem: OI) => void;
  onEdit?: (orderItem: OI) => void;
  onChange?: (orderItem: OI) => void;
  orderItem: OI;
}

const OrderItem: FC<OrderItemProps> = ({
  orderItem,
  onDelete,
  onEdit,
  onChange
}: OrderItemProps) => {
  const classes = useStyles();

  const setCount = (quantity: number) => {
    orderItem.count = quantity;
    if (!isNullOrUndefined(onChange)) {
      onChange(orderItem);
    }
  };

  const handleDelete = () => {
    if (!isNullOrUndefined(onDelete)) {
      onDelete(orderItem);
    }
  };

  const handleEdit = () => {
    if (!isNullOrUndefined(onEdit)) {
      onEdit(orderItem);
    }
  };

  const getOptionDisplayName = (
    option: OrderOption,
    modifier: OrderModifier
  ): string => {
    let displayName =
      modifier.prefix === undefined ? '' : `${modifier.prefix} ${option.name}`;

    if (option.suboptions !== undefined) {
      option.suboptions.forEach(sub => (displayName += ` ${sub.name ?? ''}`));
    }
    return displayName;
  };

  return useObserver(() => (
    <Box className={classes.orderItem}>
      <Grid container className={classes.info} direction="row">
        <Grid item>
          <Avatar>
            {isNullOrUndefined(orderItem.image_url) ? (
              <Fastfood />
            ) : (
              <Box className={classes.avatar}>
                <Image
                  disableSpinner
                  src={orderItem.image_url}
                  imageStyle={{
                    objectFit: 'cover',
                    width: '100%'
                  }}
                />
              </Box>
            )}
          </Avatar>
        </Grid>
        <Grid item container className={classes.detail} direction="column">
          <FirstRow orderItem={orderItem} />
          <NoteRow orderItem={orderItem} />
          {orderItem.IsPromotion && orderItem.promotion_name !== undefined && (
            <OptionRow
              key={orderItem.promotion_id}
              name={orderItem.promotion_name}
              amount={
                -(orderItem.promotion_price ?? 0) * (orderItem.count ?? 0)
              }
              prefix="("
              suffix=")"
            />
          )}
          {orderItem.modifiers.map(modifier =>
            modifier.options.map(option => (
              <OptionRow
                key={option.id}
                name={getOptionDisplayName(option, modifier)}
                amount={option.subtotal || 0}
              />
            ))
          )}
        </Grid>
      </Grid>
      <Grid item container className={classes.actions} direction="row">
        <Grid item>
          <QuantityField
            dense
            key={orderItem.count}
            value={orderItem.count}
            onChange={setCount}
            disabled={orderItem.IsPromotion}
          />
        </Grid>
        <Grid item className={classes.actionsSep}></Grid>
        <Grid item>
          <IconButton
            className={classes.actionButton}
            onClick={() => handleEdit()}
          >
            <EditOutlined />
          </IconButton>
          <IconButton
            className={classes.actionButton}
            onClick={() => handleDelete()}
          >
            <DeleteOutlined />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  ));
};

export default OrderItem;

const FirstRow: FC<{ orderItem: OI }> = ({ orderItem }) => {
  const classes = useStyles();
  return useObserver(() => (
    <Grid item container className={classes.detailRow}>
      <Grid item className={classes.name}>
        <Typography>{orderItem.name}</Typography>
      </Grid>
      <Grid item className={classes.amount}>
        <Typography align="right">
          <CurrencyFormat amount={orderItem.subtotal} />
        </Typography>
      </Grid>
    </Grid>
  ));
};

const NoteRow: FC<{ orderItem: OI }> = ({ orderItem }) => {
  if (
    !isEmpty(orderItem.note) ||
    (orderItem.price_note && orderItem.price_note > 0)
  ) {
    return (
      <OptionRow
        name={orderItem.note ?? ''}
        amount={roundM((orderItem.price_note ?? 0) * (orderItem.count ?? 0))}
      />
    );
  }

  return null;
};

const OptionRow: FC<{
  name: string;
  amount: number;
  prefix?: string;
  suffix?: string;
}> = ({ name, amount, prefix, suffix }) => {
  const classes = useStyles();
  return (
    <Grid item container className={classes.detailRow}>
      <Grid item className={classes.name}>
        <Typography color="textSecondary">{name}</Typography>
      </Grid>
      <Grid item className={classes.amount}>
        <Typography color="textSecondary" align="right">
          <CurrencyFormat amount={amount} prefix={prefix} suffix={suffix} />
        </Typography>
      </Grid>
    </Grid>
  );
};
